import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Phone,
  Globe,
  Mail,
  Clock,
  CheckCircle,
  Star,
  ArrowLeft,
  Share2,
} from 'lucide-react';
import { MapView } from '@/components/map/MapView';
import { ServiceBadge } from '@/components/listings/ServiceBadge';
import { StarRating } from '@/components/listings/StarRating';
import { useProvider } from '@/hooks/useProvider';
import { useProviderReviews } from '@/hooks/useProviderReviews';
import { format } from 'date-fns';

export function Provider() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation('provider');
  const { data: provider, isLoading } = useProvider(slug!);
  const { data: reviews = [] } = useProviderReviews(provider?.id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('notFound')}</h1>
        <Link to="/search" className="text-primary-600 hover:text-primary-700">
          {t('backToSearch')}
        </Link>
      </div>
    );
  }

  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div>
      {/* Hero */}
      <div className="relative h-64 md:h-80 bg-gray-200">
        {provider.coverImageUrl ? (
          <img
            src={provider.coverImageUrl}
            alt={provider.businessName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700" />
        )}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute top-4 left-4">
          <Link
            to="/search"
            className="flex items-center gap-2 px-3 py-2 bg-white/90 rounded-lg text-gray-700 hover:bg-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('backToSearch')}
          </Link>
        </div>
        <div className="absolute top-4 right-4">
          <button className="p-2 bg-white/90 rounded-lg text-gray-700 hover:bg-white transition">
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">
                  {provider.businessName}
                </h1>
                {provider.isVerified && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                    <CheckCircle className="w-4 h-4" />
                    {t('verified')}
                  </span>
                )}
              </div>
              {provider.averageRating && (
                <div className="flex items-center gap-2 mb-4">
                  <StarRating rating={provider.averageRating} showValue />
                  <span className="text-gray-500">
                    ({provider.reviewCount} {t('reviews')})
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {provider.serviceTypes.map((type) => (
                  <ServiceBadge key={type} type={type} />
                ))}
              </div>
            </div>

            {/* Description */}
            {provider.description && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('about')}
                </h2>
                <p className="text-gray-600 whitespace-pre-line">
                  {provider.description}
                </p>
              </div>
            )}

            {/* Photos */}
            {provider.galleryUrls.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('photos')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {provider.galleryUrls.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`${provider.businessName} photo ${idx + 1}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Service Area Map */}
            {provider.latitude && provider.longitude && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {t('serviceArea')}
                </h2>
                <div className="h-64 rounded-xl overflow-hidden">
                  <MapView
                    providers={[provider]}
                    center={{ lat: provider.latitude, lng: provider.longitude }}
                    zoom={12}
                  />
                </div>
              </div>
            )}

            {/* Reviews */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('reviews')} ({reviews.length})
              </h2>
              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="card p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">
                            {review.userName || t('anonymous')}
                          </p>
                          <div className="flex items-center gap-2">
                            <StarRating rating={review.rating} size="sm" />
                            <span className="text-sm text-gray-500">
                              {format(review.createdAt, 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                        {review.verifiedCustomer && (
                          <span className="text-xs text-primary-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {t('verifiedCustomer')}
                          </span>
                        )}
                      </div>
                      {review.title && (
                        <h4 className="font-medium text-gray-900 mb-1">
                          {review.title}
                        </h4>
                      )}
                      {review.content && (
                        <p className="text-gray-600">{review.content}</p>
                      )}
                      {review.response && (
                        <div className="mt-3 pl-4 border-l-2 border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">
                            {t('providerResponse')}
                          </p>
                          <p className="text-gray-600">{review.response}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">{t('noReviews')}</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24 space-y-6">
              {/* Contact */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">{t('contact')}</h3>
                <div className="space-y-3">
                  {provider.phone && (
                    <a
                      href={`tel:${provider.phone}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600"
                    >
                      <Phone className="w-5 h-5" />
                      {provider.phone}
                    </a>
                  )}
                  {provider.email && (
                    <a
                      href={`mailto:${provider.email}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600"
                    >
                      <Mail className="w-5 h-5" />
                      {provider.email}
                    </a>
                  )}
                  {provider.website && (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600"
                    >
                      <Globe className="w-5 h-5" />
                      {t('visitWebsite')}
                    </a>
                  )}
                  {provider.address && (
                    <div className="flex items-start gap-3 text-gray-600">
                      <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>
                        {provider.address}
                        <br />
                        {provider.city}, {provider.state} {provider.zipCode}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              {provider.phone && (
                <a
                  href={`tel:${provider.phone}`}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  {t('callNow')}
                </a>
              )}

              {/* Hours */}
              {provider.hours && Object.keys(provider.hours).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {t('hours')}
                  </h3>
                  <div className="space-y-2 text-sm">
                    {daysOfWeek.map((day) => {
                      const hours = provider.hours[day as keyof typeof provider.hours];
                      return (
                        <div key={day} className="flex justify-between">
                          <span className="text-gray-600 capitalize">{day}</span>
                          <span className="text-gray-900">
                            {hours ? `${hours.open} - ${hours.close}` : t('closed')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {provider.certifications.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {t('certifications')}
                  </h3>
                  <ul className="space-y-1">
                    {provider.certifications.map((cert, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
